import { Injectable } from '@angular/core';
import { AuthenticationService } from '../authentication.service';
import { DiskService } from '../disk/disk.service';
import { DB_KEY_PREFIXES } from '@omni/config/pouch-db.config';
import { DeltaService, EntityNames } from '@omni/data-services/delta/delta.service';
import { OfflineServiceBase } from '../offline/offline.service';
import { DynamicsClientService } from '@omni/data-services/dynamics-client/dynamics-client.service';
import { DateTimeFormatsService } from '../date-time-formats/date-time-formats.service';
import { FeatureActionsMap, IoConfigurationFeatureCodes } from '@omni/classes/authentication/user.class';

type BrickPosition = {
  brickPositionId: string;
  brickId: string;
  positionId: string;
}

type RawBrickPosition = {
  omnione_brickpositionid: string;
  _omnione_brick_value: string;
  _omnione_position_value: string;
  statecode: number;
}

@Injectable({
  providedIn: 'root'
})
export class BrickPositionService extends OfflineServiceBase<BrickPosition, 'brickPositionId'> {
  readonly brickPositions$ = this.raw$;
  constructor(
    disk: DiskService,
    deltaService: DeltaService,
    private readonly dynamics: DynamicsClientService,
    private readonly authService: AuthenticationService,
    dateTimeFormatsService: DateTimeFormatsService,
  ) {
    super(disk, deltaService, EntityNames.brickPosition, 'brickPositionId', DB_KEY_PREFIXES.OMNIONE_BRICK_POSITION, dateTimeFormatsService);
  }

  protected override isSyncAllowed(): boolean | Promise<boolean> {
    const config = this.authService.user?.ioConfigurations?.find(
      (c) => c.featureCode === IoConfigurationFeatureCodes.ORG_CONTACT_GA_MYTERRITORYFILTERSBRICK_CREATE_UPDATE
    );
    if (['true', 'yes', 't', 'y', '1'].includes(config?.configValue?.toLowerCase())) {
      return false;
    }

    return this.authService.hasFeatureAction(FeatureActionsMap.CONTACT_TERRITORY_PERFORMANCE);
  }

  protected async fetchData(lastUpdatedTime: string) {
    // Get user position IDs for filtering data based on user's access
    const positionIDs = this.authService.user.positions.map((o) => o.ID);

    // If user has no positions, return empty array (no data to fetch)
    if (positionIDs.length === 0) {
      return [];
    }

    // Build position condition for FetchXML query
    const positionCondition = positionIDs.length > 1 ? positionIDs.map(id => `<value>${id}</value>`).join('') : positionIDs[0];

    // Helper function to create position filter conditions for FetchXML
    const positionFilter = (entity, attribute) =>
      positionIDs.length > 1
        ? `<condition entityname="${entity}" attribute="${attribute}" operator="in">${positionCondition}</condition>`
        : `<condition entityname="${entity}" attribute="${attribute}" operator="eq" value="${positionCondition}" />`;


    const fetchXML = [
      '<fetch version="1.0" output-format="xml-platform" mapping="logical" no-lock="true" distinct="true">',
      '<entity name="omnione_brickposition">',
      '<attribute name="omnione_brickpositionid" />',
      '<attribute name="omnione_brick" />',
      '<attribute name="omnione_position" />',
      '<attribute name="statecode" />',
      '<filter>',
      lastUpdatedTime
        ? `<condition attribute="modifiedon" operator="ge" value="${lastUpdatedTime}" />`
        : `<condition attribute="statecode" operator="eq" value="0" />`,
      '<filter type="or">',
      positionFilter('pos', 'positionid'),
      positionFilter('pos', 'parentpositionid'),
      '</filter>',
      '</filter>',
      '<link-entity name="position" from="positionid" to="omnione_position" alias="pos" />',
      '</entity>',
      '</fetch>'
    ].join('');
    const response = await this.dynamics.executeFetchQuery<RawBrickPosition>('omnione_brickpositions', fetchXML);
    return response.map(o => (o.statecode === 0 ? {
      brickPositionId: o.omnione_brickpositionid,
      brickId: o._omnione_brick_value,
      positionId: o._omnione_position_value,
    } : {
      brickPositionId: o.omnione_brickpositionid,
      _deleted: true,
    }));
  }
}
